/**
 * shared/collision/boxworld.ts — tiny reference CollisionWorld made of axis-aligned boxes.
 *
 * Use it in unit tests (pmove, weapons, bots) until the real Hijacked collision lands, and as
 * the placeholder world in the skeleton. The box clip follows Q3 CM_ClipBoxToBrush (expanded
 * planes, SURFACE_CLIP_EPSILON, startsolid/allsolid) so behaviour matches the real thing.
 *
 * OWNER: collision agent (may extend; keep the API).
 */
import { ENTITYNUM_NONE, ENTITYNUM_WORLD } from '../constants';
import type { ReadonlyVec3, Vec3 } from '../math';
import {
  CONTENTS, SURFACE_CLIP_EPSILON, createTraceResult, surfaceTypeFromFlags,
  type ClipEntity, type CollisionWorld, type TraceFn, type TraceResult,
} from './types';

export interface BoxBrush {
  mins: Vec3;
  maxs: Vec3;
  contents: number;
  surfaceFlags: number;
}

/** Shared box-sweep core: clips the moving box against one static AABB, updating `tr` if closer. */
export function clipBoxToAabb(
  tr: TraceResult, start: ReadonlyVec3, end: ReadonlyVec3, mins: ReadonlyVec3, maxs: ReadonlyVec3,
  bmin: ReadonlyVec3, bmax: ReadonlyVec3, contents: number, surfaceFlags: number, entityNum: number,
): void {
  let enterFrac = -1;
  let leaveFrac = 1;
  let getout = false;
  let startout = false;
  let hitAxis = -1;
  let hitSign = 0;
  let hitDist = 0;
  for (let axis = 0; axis < 3; axis++) {
    for (let sign = -1; sign <= 1; sign += 2) {
      // plane: sign * p[axis] = dist (outward), expanded by the moving box. The trace reports the
      // unexpanded surface plane (Q3 `trace.plane = *clipplane`), like MeshCollisionWorld.
      const dist = sign > 0 ? bmax[axis] - mins[axis] : -(bmin[axis] - maxs[axis]);
      const d1 = sign * start[axis] - dist;
      const d2 = sign * end[axis] - dist;
      if (d2 > 0) getout = true;
      if (d1 > 0) startout = true;
      if (d1 > 0 && (d2 >= SURFACE_CLIP_EPSILON || d2 >= d1)) return; // completely in front
      if (d1 <= 0 && d2 <= 0) continue; // completely behind this plane
      if (d1 > d2) {
        let f = (d1 - SURFACE_CLIP_EPSILON) / (d1 - d2);
        if (f < 0) f = 0;
        if (f > enterFrac) { enterFrac = f; hitAxis = axis; hitSign = sign; hitDist = sign > 0 ? bmax[axis] : -bmin[axis]; }
      } else {
        let f = (d1 + SURFACE_CLIP_EPSILON) / (d1 - d2);
        if (f > 1) f = 1;
        if (f < leaveFrac) leaveFrac = f;
      }
    }
  }
  if (!startout) {
    tr.startsolid = true;
    if (!getout) { tr.allsolid = true; tr.fraction = 0; }
    tr.contents = contents;
    tr.entityNum = entityNum;
    return;
  }
  if (enterFrac < leaveFrac && enterFrac > -1 && enterFrac < tr.fraction) {
    tr.fraction = enterFrac < 0 ? 0 : enterFrac;
    tr.normal[0] = 0; tr.normal[1] = 0; tr.normal[2] = 0;
    tr.normal[hitAxis] = hitSign;
    tr.planeDist = hitDist;
    tr.contents = contents;
    tr.surfaceFlags = surfaceFlags;
    tr.surfaceType = surfaceTypeFromFlags(surfaceFlags);
    tr.entityNum = entityNum;
  }
}

function resetTrace(out: TraceResult): void {
  out.fraction = 1; out.startsolid = false; out.allsolid = false;
  out.normal[0] = 0; out.normal[1] = 0; out.normal[2] = 0;
  out.planeDist = 0; out.contents = 0; out.surfaceFlags = 0; out.surfaceType = 0; out.entityNum = ENTITYNUM_NONE;
}
function finishTrace(out: TraceResult, start: ReadonlyVec3, end: ReadonlyVec3): TraceResult {
  if (out.allsolid) { out.fraction = 0; }
  const f = out.fraction;
  out.endpos[0] = start[0] + f * (end[0] - start[0]);
  out.endpos[1] = start[1] + f * (end[1] - start[1]);
  out.endpos[2] = start[2] + f * (end[2] - start[2]);
  return out;
}

export class BoxWorld implements CollisionWorld {
  readonly name: string;
  readonly bounds: { mins: Vec3; maxs: Vec3 };
  readonly brushes: BoxBrush[] = [];

  constructor(name = 'boxworld', brushes: BoxBrush[] = []) {
    this.name = name;
    this.bounds = { mins: [Infinity, Infinity, Infinity], maxs: [-Infinity, -Infinity, -Infinity] };
    for (const b of brushes) this.add(b);
  }

  add(b: BoxBrush): this {
    this.brushes.push(b);
    for (let i = 0; i < 3; i++) {
      this.bounds.mins[i] = Math.min(this.bounds.mins[i], b.mins[i]);
      this.bounds.maxs[i] = Math.max(this.bounds.maxs[i], b.maxs[i]);
    }
    return this;
  }

  /** Convenience: solid box with default surface. */
  box(mins: Vec3, maxs: Vec3, contents: number = CONTENTS.SOLID, surfaceFlags = 0): this {
    return this.add({ mins, maxs, contents, surfaceFlags });
  }

  trace(
    out: TraceResult, start: ReadonlyVec3, end: ReadonlyVec3, mins: ReadonlyVec3, maxs: ReadonlyVec3,
    contentMask: number,
  ): TraceResult {
    resetTrace(out);
    for (const b of this.brushes) {
      if (!(b.contents & contentMask)) continue;
      clipBoxToAabb(out, start, end, mins, maxs, b.mins, b.maxs, b.contents, b.surfaceFlags, ENTITYNUM_WORLD);
      if (out.allsolid) break;
    }
    return finishTrace(out, start, end);
  }

  pointContents(p: ReadonlyVec3, contentMask: number): number {
    let c = 0;
    for (const b of this.brushes) {
      if (p[0] >= b.mins[0] && p[0] <= b.maxs[0] && p[1] >= b.mins[1] && p[1] <= b.maxs[1] && p[2] >= b.mins[2] && p[2] <= b.maxs[2]) c |= b.contents;
    }
    return c & contentMask;
  }
}

/** Infinite-ish flat floor whose top is at z (CoD space). surfaceType default concrete (5). */
export function createFlatWorld(z = 0, surfaceFlags = 5 << 20): BoxWorld {
  return new BoxWorld('flat').box([-65536, -65536, z - 64], [65536, 65536, z], CONTENTS.SOLID, surfaceFlags);
}

/** Empty world: nothing collides (placeholder before the real map collision loads). */
export function createEmptyWorld(): BoxWorld {
  const w = new BoxWorld('empty');
  w.bounds.mins = [-4096, -4096, -4096];
  w.bounds.maxs = [4096, 4096, 4096];
  return w;
}

/**
 * Build the TraceFn pmove/missiles use: world first, then dynamic entity boxes
 * (other players). Skips passEntityNum and entities owned by it. Q3 SV_Trace / CG_Trace pattern.
 */
export function makeTraceFn(world: CollisionWorld, getEntities: () => readonly ClipEntity[]): TraceFn {
  const tmp = createTraceResult();
  return (out, start, end, mins, maxs, contentMask, passEntityNum) => {
    world.trace(out, start, end, mins, maxs, contentMask, passEntityNum);
    if (out.allsolid) return out;
    const ents = getEntities();
    for (const e of ents) {
      if (e.entityNum === passEntityNum || (e.ownerNum !== undefined && e.ownerNum === passEntityNum)) continue;
      if (!(e.contents & contentMask)) continue;
      resetTrace(tmp);
      tmp.fraction = out.fraction;
      clipBoxToAabb(tmp, start, end, mins, maxs, e.absMins, e.absMaxs, e.contents, 29 << 20 /* player */, e.entityNum);
      if (tmp.allsolid || tmp.startsolid) {
        out.startsolid = true;
        if (tmp.allsolid) { out.allsolid = true; out.fraction = 0; out.entityNum = e.entityNum; out.contents = e.contents; break; }
      }
      if (tmp.fraction < out.fraction) {
        out.fraction = tmp.fraction;
        out.normal[0] = tmp.normal[0]; out.normal[1] = tmp.normal[1]; out.normal[2] = tmp.normal[2];
        out.planeDist = tmp.planeDist; out.contents = tmp.contents; out.surfaceFlags = tmp.surfaceFlags;
        out.surfaceType = tmp.surfaceType; out.entityNum = tmp.entityNum;
      }
    }
    return finishTrace(out, start, end);
  };
}
